import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Avatar,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import DeckList from './DeckList';
import { getAllInkColors } from './InkColorIcon';

const DeckGroupedList = ({
    groupedDecks,
    expandedArchetypes,
    onToggleArchetype,
    viewMode,
    currentUser,
    isDeckFavorite,
    getUserDeckCategory,
    getCategoryColor,
    getCategoryLabel,
    onToggleFavorite,
    onCopyDeckList,
    onEditDeck,
    onDeleteDeck,
    onCategoryChange,
    onImageClick,
    onOpenQuickMatch
}) => {
    const theme = useTheme();
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const inkColors = getAllInkColors();

    const getInkColor = (color) => {
        const colorData = inkColors.find((entry) => entry.value === color);
        return colorData?.color || '#666';
    };

    const calculateGroupStats = (decks) => {
        if (!decks || decks.length === 0) return null;

        let totalGames = 0;
        let totalWins = 0;
        let totalLosses = 0;

        decks.forEach((deck) => {
            if (deck.stats && deck.stats.overall) {
                totalGames += deck.stats.overall.games || 0;
                totalWins += deck.stats.overall.wins || 0;
                totalLosses += deck.stats.overall.losses || 0;
            }
        });

        const winrate = (totalWins + totalLosses) > 0
            ? Math.round((totalWins / (totalWins + totalLosses)) * 100)
            : 0;

        return { totalGames, winrate };
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? 1.5 : 2 }}>
            {groupedDecks.map((group) => (
                <Accordion
                    key={group.archetypeId || 'no-archetype'}
                    expanded={expandedArchetypes.has(group.archetypeId || 'no-archetype')}
                    onChange={() => onToggleArchetype(group.archetypeId || 'no-archetype')}
                    sx={{
                        backgroundColor: 'rgba(17, 24, 39, 0.2)',
                        border: '1px solid rgba(76, 29, 149, 0.2)',
                        borderRadius: '12px !important',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 }
                    }}
                >
                    <AccordionSummary
                        expandIcon={
                            <ExpandMoreIcon
                                sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: isSmallMobile ? 20 : 24 }}
                            />
                        }
                        sx={{
                            '& .MuiAccordionSummary-content': {
                                margin: isSmallMobile ? '8px 0' : '12px 0'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: isSmallMobile ? 1 : 2, width: '100%' }}>
                            {group.archetype?.ink_colors?.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.3, flexShrink: 0 }}>
                                    {group.archetype.ink_colors.map((color, index) => {
                                        const colorData = inkColors.find((entry) => entry.value === color);
                                        return (
                                            <Avatar
                                                key={index}
                                                src={colorData?.icon}
                                                sx={{
                                                    width: isSmallMobile ? 20 : 24,
                                                    height: isSmallMobile ? 20 : 24,
                                                    backgroundColor: getInkColor(color),
                                                    border: `1px solid ${getInkColor(color)}`
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            )}

                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    flex: 1,
                                    fontSize: isSmallMobile ? '0.9rem' : '1rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {group.name}
                            </Typography>

                            {(() => {
                                const groupStats = calculateGroupStats(group.decks);
                                if (!groupStats || groupStats.totalGames === 0) return null;
                                const winrateColor = groupStats.winrate >= 60 ? '#10b981' : groupStats.winrate >= 50 ? '#f59e0b' : '#ef4444';
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AssessmentIcon sx={{ color: '#7c3aed', fontSize: 14 }} />
                                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.65rem' }}>
                                                {groupStats.totalGames} gier
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={`${groupStats.winrate}%`}
                                            size="small"
                                            sx={{
                                                backgroundColor: `${winrateColor}20`,
                                                color: winrateColor,
                                                border: `1px solid ${winrateColor}40`,
                                                fontSize: '0.65rem',
                                                height: 18,
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                );
                            })()}

                            <Chip
                                label={group.decks.length}
                                size="small"
                                sx={{
                                    backgroundColor: 'rgba(124, 58, 237, 0.2)',
                                    color: '#c4b5fd',
                                    border: '1px solid rgba(124, 58, 237, 0.3)',
                                    fontSize: isSmallMobile ? '0.65rem' : '0.7rem',
                                    height: isSmallMobile ? 18 : 20,
                                    flexShrink: 0
                                }}
                            />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, pt: isSmallMobile ? 0.5 : 1, pb: 1 }}>
                        <DeckList
                            decks={group.decks}
                            viewMode={viewMode}
                            currentUser={currentUser}
                            isDeckFavorite={isDeckFavorite}
                            getUserDeckCategory={getUserDeckCategory}
                            getCategoryColor={getCategoryColor}
                            getCategoryLabel={getCategoryLabel}
                            onToggleFavorite={onToggleFavorite}
                            onCopyDeckList={onCopyDeckList}
                            onEditDeck={onEditDeck}
                            onDeleteDeck={onDeleteDeck}
                            onCategoryChange={onCategoryChange}
                            onImageClick={onImageClick}
                            onOpenQuickMatch={onOpenQuickMatch}
                        />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default DeckGroupedList;
